import { NetworkStatus } from '@apollo/client';
import {
  AvatarSize,
  Box,
  Combobox,
  Separator,
  Text,
  TextFieldAppearance,
  TextFieldSize,
  useSideSheetState,
} from '@bechtel/gds';
import { useAppUser, useDebounce, useName } from '@bechtel/ht-clients-common';
import { OperatorType, ToolOrderInventory, ToolRoom, UserProject } from '@bechtel/ht-types';
import { get } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { ErrorBoundary } from '~/components';
import { Backdrop } from '~/components/backdrop';
import CommonHeader from '~/components/molecules/common-header';
import { ErrorModal } from '~/components/molecules/error-modal';
import UserPhoto from '~/components/molecules/user-photo';
import { useSearchUserProject } from '~/hooks/use-search-user-projects';
import { useSearchToolOrderInventoryOrderFor } from '~/hooks/use-tool-inventory-order-for';
import { CreateReservationSideSheetStyled } from '~/views/reservations/create/styled';
import ReturnToolDetail from './detail';
import { ReturnToolList } from './list';
import ReturnContextWapper from './return-tool-context';
import { HeadingStyled, ReturnListSectionStyled, ReturnToolHeaderStyled } from './styled';
import { ReturnToolProps } from './types';

const EmployeeSearchItem = ({ item }: any) => {
  const { getFullName } = useName();
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }} style={{ maxWidth: '200px' }}>
      <Box mr={{ sm: '400' }}>
        <UserPhoto imageUrl={item && item?.imageUrl ? item?.imageUrl : ''} size={AvatarSize.MEDIUM}></UserPhoto>
      </Box>
      <Box flexDirection={{ sm: 'column' }}>
        <HeadingStyled>{getFullName(item?.firstName, item?.lastName)}</HeadingStyled>

        {item?.persona ? (
          <Text size="300" color="grey.600" transform="capitalize">
            {item?.persona.replace(/_/g, ' ').toLowerCase()}
          </Text>
        ) : (
          <Text size="300" color="grey.600" transform="capitalize">
            User
          </Text>
        )}
      </Box>
    </Box>
  );
};

const ReturnToolOwned: React.FC<ReturnToolProps> = ({ toolRoomId, user, toolOrderInventoryIds, onCloseCallback }) => {
  const [cartErrorMessage, setCartErrorMessage] = useState<string>('');
  const { close, visible } = useSideSheetState(true);
  const [searchText, setSearchText] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { searchUserProject, data: userProjectData, networkStatus } = useSearchUserProject();
  const debouncedSearchText = useDebounce(searchText, 500);
  const { appUser } = useAppUser();
  const [selectedUser, setSelectedUser] = useState<UserProject | null>(location.state?.selectedUser ?? user);
  const { getFullName } = useName();
  const [searchInventoryText, setSearchInventoryText] = useState('');
  const ownedInventorySearchText = useDebounce(searchInventoryText, 500);
  const [selectedToolRoomId, setSelectedToolRoomId] = useState(
    location?.state?.toolRoomId ? location?.state?.toolRoomId : toolRoomId
  );
  const { searchToolOrderInventoryOrderFor, data, loading } = useSearchToolOrderInventoryOrderFor(true);
  const itemToString = (item) => getFullName(item.firstName, item.lastName);

  useEffect(() => {
    setSelectedToolRoomId(location?.state?.toolRoomId ? location?.state?.toolRoomId : toolRoomId);
  }, [toolRoomId, location?.state?.toolRoomId]);

  useEffect(() => {
    if (!selectedUser) {
      setCartErrorMessage('User detail is missing');
      return;
    }
    searchToolOrderInventoryOrderFor(selectedUser?.id);
  }, [searchToolOrderInventoryOrderFor, selectedUser]);

  useEffect(() => {
    if (searchText.length < 3) {
      return;
    }
    searchUserProject({
      sortInput: [],
      filterInput: {
        or: [
          {
            field: 'firstName',
            operator: OperatorType.LIKE,
            value: searchText,
          },
          {
            field: 'lastName',
            operator: OperatorType.LIKE,
            value: searchText,
          },
          {
            field: 'lobEmployeeId',
            operator: OperatorType.LIKE,
            value: searchText,
          },
          {
            field: 'badgeBarcodeId',
            operator: OperatorType.LIKE,
            value: searchText,
          },
        ],
        and: [{ field: 'projectId', value: appUser?.defaultProjectId, operator: OperatorType.EQUAL }],
      },
    });
  }, [appUser?.defaultProjectId, debouncedSearchText, searchText, searchUserProject]);

  const userList = get(userProjectData, 'searchUserProjects.items', []);

  let list: ToolOrderInventory[] = get(data, 'searchToolOrderInventoryByOrderedFor.items', []);

  const searchItemsItems = (item) => {
    const items =
      (list && item.toolInventory.barCode.toLowerCase().includes(ownedInventorySearchText.toLowerCase())) ||
      (list && item.toolInventory.description.toLowerCase().includes(ownedInventorySearchText.toLowerCase()));
    null;
    return items;
  };
  if (ownedInventorySearchText) {
    list = list?.filter(searchItemsItems) ?? null;
  } else {
    list = list ?? null;
  }

  const groupToolOrderInventory = (list: ToolOrderInventory[] | null) => {
    let serialisedTools =
      list &&
      list.filter((item: ToolOrderInventory) => item && item.toolInventory && item.toolInventory.isSerialized === true);
    let consumerableTools =
      list &&
      list.filter(
        (item: ToolOrderInventory) => item && item.toolInventory && item.toolInventory.isSerialized === false
      );
    if (toolOrderInventoryIds && toolOrderInventoryIds.length > 0) {
      serialisedTools =
        serialisedTools &&
        serialisedTools.filter(
          (item: ToolOrderInventory) => item && item.dispatchQuantity > 0 && toolOrderInventoryIds.includes(item.id)
        );
      consumerableTools =
        consumerableTools &&
        consumerableTools.filter(
          (item: ToolOrderInventory) => item && item.dispatchQuantity > 0 && toolOrderInventoryIds.includes(item.id)
        );
    }
    return [serialisedTools, consumerableTools];
  };

  const [serialisedTools, consumerableTools] = groupToolOrderInventory(list);

  const closeModal = () => {
    setCartErrorMessage('');
  };

  const onToolRoomChange = (toolRoom: ToolRoom) => {
    setSelectedToolRoomId(toolRoom.id);
  };

  const onClose = () => {
    if (toolRoomId) {
      onCloseCallback && onCloseCallback();
    } else {
      close();
      navigate('/returns');
    }
  };

  return (
    <ErrorBoundary>
      {loading && <Backdrop></Backdrop>}
      <CreateReservationSideSheetStyled onDismiss={close} visible={visible}>
        <CommonHeader
          leftHeadering="Start New Returns"
          selectedToolRoomId={selectedToolRoomId}
          onToolRoomChange={onToolRoomChange}
          onClose={onClose}
        />

        <ReturnToolHeaderStyled pl={{ sm: '550' }} pr={{ sm: '700' }} pt={{ sm: '400' }} pb={{ sm: '400' }}>
          <Box>
            <Box alignItems={{ sm: 'center' }}>Check in for</Box>
            <Box pl={{ sm: '400' }} style={{ width: '220px', maxWidth: '220px' }}>
              <Combobox
                placeholder="Search User"
                size={TextFieldSize.LARGE}
                items={userList}
                appearance={TextFieldAppearance.TRANSPARENT}
                itemString={(item) => itemToString(item)}
                itemComponent={(item) => EmployeeSearchItem(item)}
                selectedComponent={(item) => EmployeeSearchItem(item)}
                selectedItem={selectedUser}
                onSelect={(item: any) => {
                  setSelectedUser(item);
                }}
                term={searchText}
                setTerm={setSearchText}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchText(event.target.value);
                }}
                restorePreviousOnSkip
                loading={loading || networkStatus === NetworkStatus.refetch}
                messageOnNoResults={`No users found`}
              />
            </Box>
          </Box>
          <Box>
            <Text family="openSansSemiBold">
              {serialisedTools && serialisedTools.length > 0 ? `${serialisedTools.length} Tools Owned` : ''}
            </Text>
          </Box>
        </ReturnToolHeaderStyled>
        <Separator spacing="000" color="grey.300" />

        <Box>
          <ReturnContextWapper>
            <ReturnListSectionStyled>
              <ReturnToolList
                onSearch={setSearchInventoryText}
                consumerableTools={consumerableTools}
                serialisedTools={serialisedTools}
                isExistingOrder={toolOrderInventoryIds && toolOrderInventoryIds.length > 0}
              />
            </ReturnListSectionStyled>

            <ReturnToolDetail selectedUser={selectedUser} selectedToolRoomId={toolRoomId} onClose={onClose} />
          </ReturnContextWapper>
        </Box>
        {cartErrorMessage && (
          <ErrorModal close={closeModal} message={cartErrorMessage} title="Validation Message" closeText="close" />
        )}
      </CreateReservationSideSheetStyled>
    </ErrorBoundary>
  );
};

export default ReturnToolOwned;
